<template>
    <!-- might need 'first' class adding to first item -->
    <div data-component="OrderDetailsMembership"
         class="order-details-membership"
         :data-membership-id="itemInstance.getMembership().getID()">
        <div class="order-details-membership__info-row">
            <!-- Membership Ref -->
            <a class="btn btn-info order-details-membership__ref"
               data-testid="OrderDetailsMembershipRefLink"
               :disabled="itemInstance.getMembership().isDead() ? true : null"
               @click.prevent="clearRenewingAndViewMembership()">
                {{ itemInstance.getMembership().getRef() }}
            </a>

            <!-- Membership Status -->
            <a class="btn order-details-membership__status"
               data-testid="OrderDetailsMembershipStatusLink"
               :disabled="itemInstance.getMembership().isDead() ? true : null"
               :class="{
                   'btn-warning': itemInstance.getMembership().isPostponed(),
                   'btn-success': itemInstance.getMembership().getStatus() === 'ACTIVE',
                   'btn-danger': itemInstance.getMembership().getStatus() != 'ACTIVE'
               }"
               @click.prevent="clearRenewingAndViewMembership()">
                {{ l('MEMBERSHIP_DETAILS.' + itemInstance.getMembership().getStatus()) }}
            </a>

            <!-- Edit Button -->
            <button class="btn btn-default order-details-membership__edit-btn"
                    data-testid="OrderDetailsMembershipEditButton"
                    :disabled="isEditButtonDisabled ? true : null"
                    title="Edit the membership details"
                    @click.prevent="editMembership()">
                <i class="fa-solid fa-pen-to-square"></i>
            </button>
        </div>

        <!-- Redeemption buttons row -->
        <div class="order-details-membership__btns order-details-membership__redeem-btns">
            <button :disabled="isUnlogButtonDisabled ? true : null"
                    data-testid="OrderDetailsMembershipUnlogVisitButton"
                    class="btn btn-lg btn-danger"
                    type="button"
                    @click.prevent="unlogVisit()">
                <i class="fa-solid fa-circle-minus"></i>
            </button>

            <div class="text-center">
                <span :class="[itemInstance.membershipSubscription.isAtMaximumVisits() ? 'text-danger' : '']"
                      data-testid="OrderDetailsMembershipDisplayVisits">
                    {{ itemInstance.membershipSubscription.getVisits() }}
                    {{ l('MEMBERSHIP_DETAILS.VISITS', { count: itemInstance.membershipSubscription.getVisits() }) }}
                </span>
            </div>

            <button :disabled="isLogButtonDisabled ? true : null"
                    data-testid="OrderDetailsMembershipLogVisitButton"
                    class="btn btn-lg btn-success"
                    type="button"
                    @click.prevent="logVisit()">
                <i class="fa-solid fa-circle-plus"></i>
            </button>
        </div>

        <!-- Show reasons why the membership cannot be redeemed. -->
        <MembershipUnredeemableInfo v-if="itemInstance.getMembership()"
                                    :membership="itemInstance.getMembership()"
                                    :subscription="itemInstance.membershipSubscription" />

        <!-- Renewal buttons row -->
        <div v-if="itemInstance.getMembership().isRenewable()"
             class="order-details-membership__btns desktop-only"
             data-testid="RenewalButtonGroup">
            <button class="btn btn--inline-flex"
                    data-testid="RenewalButtonGroupChangeMembershipRenewingStateButton"
                    :class="itemInstance.getMembership().isRenewing() ? 'btn-success active' : 'btn-primary'"
                    @click.prevent="changeMembershipRenewingState()">
                <span>
                    <i class="fa-solid fa-arrow-rotate-right"></i>
                    {{
                        l(itemInstance.getMembership().getRenewButtonText(),
                          {
                              from: itemInstance.getMembership().getChosenRenewalFromDate(),
                              nextEndDate: itemInstance.getMembership().renewToDate
                          })
                    }}
                    ({{ currencyFilter(itemInstance.getMembership().renewalPlan.getPrice()) }})
                </span>
            </button>
        </div>

        <!-- Members row -->
        <div v-if="!itemInstance.getMembership().isDead()"
             class="order-details-membership__members">
            <template v-for="member in itemInstance.getMembership().getMembers()"
                      :key="member.ID">
                <div v-if="member.hasContact()"
                     :key="'member-' + member.getID()"
                     class="order-details-membership__member"
                     :data-testid="`OrderDetailsMembershipMember-${member.getID()}`"
                     @click.prevent="toggleMemberVisit(member)">
                    <ContactPhoto :contact="member.contact"
                                  :width="96"
                                  :height="72"
                                  class="order-details-membership__img"></ContactPhoto>

                    <div>
                        <ContactNameWithTitle :contact="member.contact"
                                              class="order-details-membership__member__name" />
                        <span v-if="member.contact.ref"
                              class="block"
                              :data-testid="`OrderDetailsMembershipMemberContact-${member.contact.ref}`">
                            {{ member.contact.ref }}
                            <strong>&bull;</strong>
                            {{ member.getVisits() }}
                            {{ l('MEMBERSHIP_DETAILS.VISITS', { count: member.getVisits()}) }}
                        </span>

                        <!-- Admit one member button -->
                        <!-- This button does emit an event but we don't listen to that event in this context -->
                        <MemberRedemptionButton :membership="itemInstance.getMembership()"
                                                :member="member" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import ContactNameWithTitle from '@/epos/vue/components/Contacts/ContactNameWithTitle';
import ContactPhoto from '@/epos/vue/components/Customers/ContactPhoto';
import EventNames from '@/lib/Events/EventNames';
import MemberRedemptionButton from '@/epos/vue/components/Memberships/MemberRedemptionButton';
import MembershipUnredeemableInfo from '@/epos/vue/components/Memberships/MembershipUnredeemableInfo';
import { container } from '@/container';

export default {
    name: 'OrderDetailsMembership',

    components: {
        MemberRedemptionButton,
        ContactNameWithTitle,
        ContactPhoto,
        MembershipUnredeemableInfo,
    },

    props: {
        itemInstance: {
            type: Object,
            required: true,
        },
        orderItem: {
            type: Object,
            required: true,
        },
        currentGroup: {
            type: Object,
            required: true,
        },
    },

    computed: {
        isEditButtonDisabled() {
            return this.itemInstance.getMembership().currentlyProcessing
                || !this.itemInstance.getMembership().isActive();
        },

        isUnlogButtonDisabled() {
            return this.itemInstance.membershipSubscription.getVisits() <= 0
                || !this.itemInstance.getMembership().canBeRedeemedRightNow()
                || !this.itemInstance.getMembership().hasRedeemedMembersToday();
        },

        isLogButtonDisabled() {
            return this.itemInstance.membershipSubscription.isAtMaximumVisits()
                || !this.itemInstance.getMembership().canBeRedeemedRightNow()
                || !this.itemInstance.getMembership().hasNonRedeemedMembersToday();
        },
    },

    methods: {
        changeMembershipRenewingState() {
            container.membershipManagementService.changeMembershipRenewingState(this.itemInstance.getMembership(), true);
        },

        /**
         * Clears the renewing state and views membership details.
         */
        clearRenewingAndViewMembership() {
            container.eventBus.broadcast(
                EventNames.ORDER_DETAILS_MEMBERSHIP_CLEAR_RENEWING_AND_VIEW_MEMBERSHIP, {
                    ref: this.itemInstance.getMembership().getRef(),
                    groupID: this.currentGroup.ID,
                }
            );
        },

        editMembership() {
            container.eventBus.broadcast(
                EventNames.ORDER_DETAILS_MEMBERSHIP_EDIT_MEMBERSHIP,
                {
                    orderLineID: this.orderItem.ID,
                    itemInstanceID: this.itemInstance.ID,
                }
            );
        },

        /**
         * Logs a new visit to the membership subscription.
         */
        logVisit() {
            container.eventBus.broadcast(
                EventNames.ORDER_DETAILS_MEMBERSHIP_LOG_VISIT,
                {
                    orderLineID: this.orderItem.ID,
                    itemInstanceID: this.itemInstance.ID,
                }
            );
        },

        /**
         * Handles unlogging a visit from the membership subscription.
         */
        unlogVisit() {
            container.eventBus.broadcast(
                EventNames.ORDER_DETAILS_MEMBERSHIP_UNLOG_VISIT,
                {
                    orderLineID: this.orderItem.ID,
                    itemInstanceID: this.itemInstance.ID,
                }
            );
        },

        /**
         * Manages the state when a member visit is added (emitted by v-for iteration).
         *
         * @param {Member} member
         */
        toggleMemberVisit(member) {
            container.eventBus.broadcast(
                EventNames.ORDER_DETAILS_MEMBERSHIP_TOGGLE_MEMBER_VISIT,
                {
                    memberID: member.ID,
                    orderLineID: this.orderItem.ID,
                    itemInstanceID: this.itemInstance.ID,
                }
            );
        },
    },
};
</script>

<style>
.order-details-membership__img {
    width: 96px;
    height: 72px;
    margin-right: 10px;
    border-radius: 3px;
}
</style>
